import * as THREE from 'three';
import { ABILITY_FX_DURATION_MS, AbilityFxKind } from '../config/ability-vfx';

/**
 * Visualizing abilities (2026-08-26) — the world half of an ability's
 * identity. `config/ability-vfx.ts` says what an ability looks like; this
 * draws it.
 *
 * A scene-level layer rather than more methods on `CharacterVisual`, because
 * half of these effects are not attached to one body: a Moonfire travels
 * between two of them, a Taunt is a line drawn between two of them, and a
 * Snare belongs to the body that got rooted rather than to the druid who cast
 * it. Effects that *are* body-attached (the ward shell, the stealth
 * implosion) still live here so that one update loop owns their lifetime —
 * the alternative is a caster who disconnects mid-ward leaving a shell
 * orbiting nothing.
 *
 * **Anchors are functions, not positions.** Everything in this game is moving:
 * a rooted skeleton is still being pushed around by knockback, a healed
 * squadmate keeps running, and a 3-second ward has to stay on the knight for
 * all three seconds. Passing a getter means an effect re-reads the body's
 * live position every frame and can also see it *vanish* — a null anchor ends
 * the effect rather than freezing it in mid-air over an entity the interest
 * manager culled.
 */

/** Where an effect is drawn, re-asked every frame. Null means the entity is gone — the effect ends. */
export type FxAnchor = () => { x: number; z: number } | null;

/**
 * Generated art (2026-08-26) — the eight painted sprites the world effects are
 * drawn with, under `FX_SPRITE_BASE`.
 *
 * Greyscale on black, and used with additive blending, which is why they need
 * no alpha channel at all: black *is* transparent under addition, so a WebP
 * with no alpha costs a third of a PNG with one and composites identically.
 * Each is tinted by the ability's own `colorHex` through the material, so one
 * ring texture serves a knight's pale-blue Brace and a barbarian's amber
 * Warcry — the colour stays the ability's, the shape stays the archetype's.
 */
export type FxSprite =
  | 'glow' | 'burst' | 'ring' | 'sweep' | 'smoke' | 'crackring' | 'thorns' | 'sparkles';

export const FX_SPRITE_BASE = '/assets/vfx';

/**
 * What the layer was given. Partial and optional throughout: every effect has
 * a procedural fallback, so a texture that has not arrived (or a test with no
 * loader at all) draws the old geometry rather than nothing. That is not a
 * nicety — an additive material with an unloaded map renders black, which
 * under addition is *invisible*, and an ability that shows nothing reads to
 * the player as an input that was dropped.
 */
export type FxSpriteSet = Partial<Record<FxSprite, THREE.Texture>>;

const FX_SPRITE_NAMES: readonly FxSprite[] = [
  'glow', 'burst', 'ring', 'sweep', 'smoke', 'crackring', 'thorns', 'sparkles',
];

/**
 * Loads the sprite set. Call once per scene and hand it to the layer, which
 * owns it from then on and disposes it with itself.
 *
 * Failures are swallowed to `undefined` rather than thrown: a missing effect
 * texture must never be the reason a dungeon fails to start.
 */
export function loadAbilityFxSprites(loader = new THREE.TextureLoader()): FxSpriteSet {
  const set: FxSpriteSet = {};
  for (const name of FX_SPRITE_NAMES) {
    // Entered into the set **on arrival**, not on request. An effect that goes
    // off while the file is still in flight has to draw the procedural shape,
    // because an additive material whose map has not decoded yet renders black
    // — which under addition is nothing at all. The rig caught exactly that:
    // the first rendered frame after a scene opens had no effects in it.
    loader.load(`${FX_SPRITE_BASE}/${name}.webp`, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      set[name] = texture;
    }, undefined, () => { delete set[name]; });
  }
  return set;
}

export interface AbilityFxRequest {
  kind: AbilityFxKind;
  colorHex: number;
  /** The caster. */
  origin: FxAnchor;
  /** The subject, for the kinds that have one. */
  target?: FxAnchor;
  /** The effect's true radius in metres, straight off the server. Only SHOUT and SWEEP read it. */
  radiusM?: number;
  /** How long to draw, in ms. For WARD and SNARE this is the real duration of the state, so it must not be shortened. */
  durationMs: number;
}

/** 0 → 1 across the effect's life. Clamped, so a frame spike cannot overshoot into a negative scale. */
export function fxProgress(elapsedMs: number, durationMs: number): number {
  if (durationMs <= 0) return 1;
  return Math.min(1, Math.max(0, elapsedMs / durationMs));
}

/**
 * A ring that starts at the caster and ends at the reported radius.
 *
 * Eased out rather than linear: the pulse should read as *leaving* the caster
 * fast and settling on its true extent, so the player's eye catches the edge
 * where it stops — that edge is the information (who was in it), and a linear
 * ring spends most of its life nowhere near it.
 */
export function shoutRadiusAt(t: number, radiusM: number): number {
  return radiusM * (1 - (1 - t) * (1 - t));
}

/**
 * A held state's opacity: in fast, hold, out over the last quarter.
 *
 * The hold is the point. A ward drawn as a single flash would say "something
 * happened", when what the player needs to know is "he is *still* immune" —
 * so it stays lit for as long as the server said the state lasts, and only
 * gets out of the way at the end.
 */
export function heldOpacityAt(t: number, peak: number): number {
  const FADE_IN = 0.08;
  const FADE_OUT = 0.25;
  if (t < FADE_IN) return peak * (t / FADE_IN);
  if (t > 1 - FADE_OUT) return peak * ((1 - t) / FADE_OUT);
  return peak;
}

/** A one-shot flourish: bright immediately, gone smoothly. */
export function burstOpacityAt(t: number, peak: number): number {
  return peak * (1 - t) * (1 - t);
}

/** Where a travelling mote is between caster and subject. Reaches the subject exactly, never near it. */
export function boltPositionAt(
  from: { x: number; z: number }, to: { x: number; z: number }, t: number,
): { x: number; z: number } {
  return { x: from.x + (to.x - from.x) * t, z: from.z + (to.z - from.z) * t };
}

/** Chest height, in metres — where a burst reads as landing on a body rather than at its feet. */
const CHEST_M = 1.15;
/** Just off the floor, so a ground ring does not z-fight with the tiles. */
const GROUND_M = 0.05;

/**
 * Geometry is shared and never disposed per effect; materials are per effect
 * because each one animates its own opacity. Building a RingGeometry on every
 * cast would allocate in the middle of a fight, which CLAUDE.md's mobile
 * budget explicitly rules out — so every shape here is a unit shape, scaled.
 */
const GEOMETRY = {
  /** Unit radius, so `scale = radiusM` gives the true footprint. */
  ring: new THREE.RingGeometry(0.86, 1, 40),
  /** A 70° slice of the same ring — the sweep. */
  arc: new THREE.RingGeometry(0.4, 1, 24, 1, 0, Math.PI * 0.39),
  sphere: new THREE.SphereGeometry(1, 14, 10),
  /**
   * Deliberately coarse — this one is only ever drawn as wireframe, and at
   * the sphere's own 14x10 the lattice reads as a mesh cage rather than as a
   * shield. Fewer, longer facets look built rather than woven.
   */
  shell: new THREE.SphereGeometry(1, 9, 6),
  spike: new THREE.ConeGeometry(0.08, 0.5, 5),
  /** A unit-length beam along +Y, so it can be scaled and aimed between two points. */
  beam: new THREE.CylinderGeometry(0.035, 0.035, 1, 6, 1, true),
  /**
   * The sprite plate. 2x2 rather than 1x1 **on purpose**: every shape in this
   * file is a unit-*radius* shape, so `scale.setScalar(r)` means "r metres
   * across the radius". A 2x2 plane obeys that same convention, which is what
   * lets a textured ring drop in for `RingGeometry` without touching a single
   * number in `step()`.
   */
  quad: new THREE.PlaneGeometry(2, 2),
};

/** A drawn part: a flat plate in the world, or a billboard that always faces the camera. */
type FxNode = THREE.Mesh | THREE.Sprite;

interface LiveEffect {
  kind: AbilityFxKind;
  group: THREE.Group;
  materials: THREE.Material[];
  request: AbilityFxRequest;
  elapsedMs: number;
  /** Per-kind parts the update step needs to reach without re-querying children. */
  parts: {
    ring?: FxNode;
    arc?: FxNode;
    shell?: THREE.Mesh;
    mote?: FxNode;
    beam?: THREE.Mesh;
    risers?: FxNode[];
    spikes?: THREE.Group;
    /** The painted impact plate, when there is a texture for it. Sized alongside `shell`. */
    burst?: FxNode;
    /** The painted smoke plume a SHROUD collapses into, when there is a texture for it. */
    smoke?: FxNode;
  };
  /** Set when a BOLT has landed and spawned its impact, so it only happens once. */
  landed?: boolean;
}

export class AbilityFxLayer {
  private readonly root = new THREE.Group();
  private readonly live: LiveEffect[] = [];

  /**
   * `sprites` defaults to empty so every existing caller — and every test —
   * keeps getting the procedural shapes. Pass `loadAbilityFxSprites()` to get
   * the painted ones. The layer owns whatever it is given and disposes it.
   */
  constructor(private readonly scene: THREE.Scene, private readonly sprites: FxSpriteSet = {}) {
    // Rendered after the world, never occluding it: every material here is
    // additive and depth-write-free, which is what makes a shell read as
    // light around a body rather than as a bag over its head.
    this.root.renderOrder = 2;
    this.scene.add(this.root);
  }

  spawn(request: AbilityFxRequest): void {
    const origin = request.origin();
    if (!origin) return;
    const group = new THREE.Group();
    const materials: THREE.Material[] = [];
    const parts: LiveEffect['parts'] = {};
    const color = new THREE.Color(request.colorHex);

    const material = (opacity: number, map?: THREE.Texture): THREE.MeshBasicMaterial => {
      const m = new THREE.MeshBasicMaterial({
        // Spread rather than `map` — three warns on an explicitly undefined
        // parameter, and the untextured branch is the common one.
        ...(map ? { map } : {}),
        color, transparent: true, opacity, depthWrite: false,
        blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      });
      materials.push(m);
      return m;
    };

    /**
     * A flat plate lying on the ground (or at `y`), painted if there is a
     * texture for it and drawn as `fallback` geometry if there is not.
     *
     * The quad is unit-radius (see `GEOMETRY.quad`), so both branches answer
     * `scale.setScalar(r)` identically and `step()` never has to know which
     * one it got.
     */
    const flat = (
      sprite: FxSprite, opacity: number, fallback: THREE.BufferGeometry, y = GROUND_M,
    ): THREE.Mesh => {
      const map = this.sprites[sprite];
      const mesh = new THREE.Mesh(map ? GEOMETRY.quad : fallback, material(opacity, map));
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = y;
      return mesh;
    };

    /**
     * A billboard that always faces the camera — for the effects that are
     * light rather than a mark on the floor: an impact flash, a travelling
     * mote, motes rising off a healed body, a plume of smoke.
     *
     * A camera-facing sprite rather than a plate for the reason the STRIKE
     * shockwave was laid flat in the first place: a quad with a fixed
     * orientation vanishes edge-on from half the angles a player might be
     * watching from. `scale` here is width in metres, not a radius.
     */
    const billboard = (sprite: FxSprite, opacity: number, scale: number): THREE.Sprite | null => {
      const map = this.sprites[sprite];
      if (!map) return null;
      const m = new THREE.SpriteMaterial({
        map, color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending,
      });
      materials.push(m);
      const node = new THREE.Sprite(m);
      node.scale.setScalar(scale);
      return node;
    };

    switch (request.kind) {
      case 'WARD': {
        const shell = new THREE.Mesh(GEOMETRY.shell, material(0));
        shell.scale.setScalar(0.82);
        shell.position.y = 0.95;
        // A lattice, not a bubble.
        //
        // The first version was a solid additive sphere, and the first live
        // capture showed exactly what the level-up pillar taught once
        // already: an additive surface between the camera and the body
        // bleaches the body out. Worse here, because a ward lasts seconds
        // rather than flashing — the knight spent his whole Brace inside a
        // white balloon. Wireframe keeps the shape and gives the body back:
        // the lines read as a shield and the gaps are most of the sphere.
        (shell.material as THREE.MeshBasicMaterial).wireframe = true;
        parts.shell = shell;
        const ring = flat('ring', 0, GEOMETRY.ring);
        parts.ring = ring;
        group.add(shell, ring);
        break;
      }
      case 'SHROUD': {
        const shell = new THREE.Mesh(GEOMETRY.shell, material(0.5));
        shell.scale.setScalar(1.6);
        shell.position.y = 1;
        (shell.material as THREE.MeshBasicMaterial).wireframe = true;
        parts.shell = shell;
        group.add(shell);
        // The lattice says *collapsing inward*, which is the read; the plume
        // says *gone into smoke*, which is the flavour. Both, not one — the
        // collapse alone was legible but bloodless for a rogue vanishing.
        const smoke = billboard('smoke', 0.5, 2.2);
        if (smoke) { smoke.position.y = 1.1; parts.smoke = smoke; group.add(smoke); }
        break;
      }
      case 'SHOUT': {
        const ring = flat('ring', 0.85, GEOMETRY.ring);
        ring.scale.setScalar(0.01);
        parts.ring = ring;
        group.add(ring);
        break;
      }
      case 'SWEEP': {
        const arc = flat('sweep', 0.8, GEOMETRY.arc, GROUND_M + 0.35);
        parts.arc = arc;
        group.add(arc);
        break;
      }
      case 'STRIKE': {
        // The painted flash if there is one, the additive sphere if not. Only
        // ever one of the two: two overlapping cores at the same point read as
        // a rendering fault rather than as a harder hit.
        const burst = billboard('burst', 0.9, 0.55);
        if (burst) {
          burst.position.y = CHEST_M;
          // Drawn through the body it landed on. The flash sits at the
          // *centre* of an entity, so the near half of that entity is between
          // it and the camera and a depth-tested sprite is hidden by the very
          // thing it is meant to be marking — the rig showed only the edges of
          // it. The cost is that an impact stays visible through a wall for
          // 380ms; the benefit is that a hit you landed always reads, and a
          // hit that does not read is the bug this whole pass exists to fix.
          burst.material.depthTest = false;
          burst.renderOrder = 3;
          parts.burst = burst;
          group.add(burst);
        } else {
          const shell = new THREE.Mesh(GEOMETRY.sphere, material(0.9));
          shell.scale.setScalar(0.22);
          shell.position.y = CHEST_M;
          parts.shell = shell;
          group.add(shell);
        }
        // Flat, at chest height: a shockwave off the body. Upright, it would
        // face one arbitrary world direction and vanish edge-on from half the
        // angles the player might be watching from.
        const ring = flat('crackring', 0.8, GEOMETRY.ring, CHEST_M);
        ring.scale.setScalar(0.3);
        parts.ring = ring;
        group.add(ring);
        break;
      }
      case 'BOLT': {
        const glow = billboard('glow', 0.95, 0.8);
        const mote = glow ?? new THREE.Mesh(GEOMETRY.sphere, material(0.95));
        if (!glow) mote.scale.setScalar(0.16);
        mote.position.y = CHEST_M;
        parts.mote = mote;
        group.add(mote);
        break;
      }
      case 'BLESSING': {
        const risers = [0, 1, 2].map((i): FxNode => {
          // Sparkles when there is art for them, rings when there is not —
          // either way three of them, out of phase, because one column reads
          // from one angle and three read from all of them.
          // Wider than it looks like it should be: the sparkle plate is mostly
          // empty space, so a 1.5m sprite draws about 0.4m of actual motes.
          const sparkle = billboard('sparkles', 0, 2.4 - i * 0.35);
          const riser = sparkle ?? flat('sparkles', 0, GEOMETRY.ring);
          if (!sparkle) riser.scale.setScalar(0.45 - i * 0.08);
          riser.position.y = GROUND_M;
          return riser;
        });
        parts.risers = risers;
        group.add(...risers);
        break;
      }
      case 'SNARE': {
        const ring = flat('thorns', 0, GEOMETRY.ring);
        ring.scale.setScalar(0.75);
        parts.ring = ring;
        group.add(ring);
        // Six cones stood in for a bramble cage before there was one painted.
        // With the texture, they are redundant *and* wrong — thorns drawn
        // twice at different scales — so they are only built as the fallback.
        if (!this.sprites.thorns) {
          const spikes = new THREE.Group();
          for (let i = 0; i < 6; i += 1) {
            const spike = new THREE.Mesh(GEOMETRY.spike, material(0));
            const angle = (i / 6) * Math.PI * 2;
            spike.position.set(Math.cos(angle) * 0.6, 0.25, Math.sin(angle) * 0.6);
            spike.rotation.z = Math.cos(angle) * 0.35;
            spike.rotation.x = -Math.sin(angle) * 0.35;
            spikes.add(spike);
          }
          parts.spikes = spikes;
          group.add(spikes);
        }
        break;
      }
      case 'LINK': {
        const beam = new THREE.Mesh(GEOMETRY.beam, material(0.85));
        parts.beam = beam;
        group.add(beam);
        break;
      }
    }

    group.position.set(origin.x, 0, origin.z);
    this.root.add(group);
    this.live.push({ kind: request.kind, group, materials, request, elapsedMs: 0, parts });
  }

  update(dtSeconds: number): void {
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      const effect = this.live[i];
      effect.elapsedMs += dtSeconds * 1000;
      if (!this.step(effect)) {
        this.disposeEffect(effect);
        this.live.splice(i, 1);
      }
    }
  }

  /** Returns false when the effect is finished — its time ran out, or the body it belonged to went away. */
  private step(effect: LiveEffect): boolean {
    const { request, parts } = effect;
    const t = fxProgress(effect.elapsedMs, request.durationMs);
    const origin = request.origin();
    const target = request.target?.() ?? null;
    // `origin` is simply where the effect is drawn — the caller decides whose
    // body that is, which for a STRIKE or a SNARE is the entity that got hit
    // rather than the one that cast. A LINK or a BOLT additionally needs its
    // far end, and half a line drawn to an entity that despawned is worse
    // than no line.
    if (!origin) return false;
    if (NEEDS_BOTH_ENDS.has(request.kind) && !target) return false;
    effect.group.position.set(origin.x, 0, origin.z);

    const opacity = (value: number) => { for (const m of effect.materials) (m as THREE.MeshBasicMaterial).opacity = value; };

    switch (request.kind) {
      case 'WARD': {
        const peak = heldOpacityAt(t, 0.3);
        opacity(peak);
        // A slow breath rather than a static bubble: a still shell stops
        // being read after the first second, which is most of a 3s Brace.
        const breathe = 1 + Math.sin(effect.elapsedMs / 220) * 0.04;
        parts.shell?.scale.setScalar(0.95 * breathe);
        parts.ring?.scale.setScalar(0.8 * breathe);
        break;
      }
      case 'SHROUD': {
        // Collapses inward — the opposite of every other effect here, which
        // is what makes it read as disappearing rather than as going off.
        const scale = 1.6 - 1.35 * t;
        parts.shell?.scale.setScalar(Math.max(0.05, scale));
        // The plume goes the other way: it swells and rises as the body
        // shrinks out of it, which is what sells one becoming the other.
        parts.smoke?.scale.setScalar(2.2 + t * 1.1);
        if (parts.smoke) parts.smoke.position.y = 1.1 + t * 0.5;
        opacity(burstOpacityAt(t, 0.55));
        break;
      }
      case 'SHOUT': {
        const radius = shoutRadiusAt(t, request.radiusM ?? 1);
        parts.ring?.scale.setScalar(Math.max(0.01, radius));
        opacity(burstOpacityAt(t, 0.85));
        break;
      }
      case 'SWEEP': {
        const radius = request.radiusM ?? 1;
        if (parts.arc) {
          parts.arc.scale.setScalar(radius);
          // A full turn over the effect's life, so the arc traces the whole
          // circle it damaged rather than sitting in one place.
          parts.arc.rotation.z = -t * Math.PI * 2;
        }
        opacity(burstOpacityAt(t, 0.8));
        break;
      }
      case 'STRIKE': {
        const grow = 0.22 + t * 0.5;
        parts.shell?.scale.setScalar(grow);
        // A sprite's number is a width where the sphere's was a radius, and
        // the painted flash falls off softly at its edge — so it needs to be
        // drawn wider than the geometry it replaced to land as hard.
        parts.burst?.scale.setScalar(grow * 2.4);
        parts.ring?.scale.setScalar(0.3 + t * 1.1);
        opacity(burstOpacityAt(t, 0.9));
        break;
      }
      case 'BOLT': {
        if (!origin || !target) return false;
        const at = boltPositionAt(origin, target, t);
        effect.group.position.set(at.x, 0, at.z);
        opacity(0.95);
        if (t >= 1 && !effect.landed) {
          effect.landed = true;
          // The mote arriving is the moment of impact — hand it to a STRIKE
          // so a spell lands with the same punctuation a swing does.
          this.spawn({
            kind: 'STRIKE', colorHex: request.colorHex, origin: request.target ?? request.origin,
            durationMs: ABILITY_FX_DURATION_MS.STRIKE,
          });
        }
        break;
      }
      case 'BLESSING': {
        // Three rings rising out of phase — a body being lifted, read from any
        // angle, which a single column is not.
        parts.risers?.forEach((ring, index) => {
          const phase = (t + index / 3) % 1;
          ring.position.y = GROUND_M + phase * 2.1;
          const m = ring.material as THREE.MeshBasicMaterial;
          m.opacity = burstOpacityAt(phase, 0.7) * (1 - t * 0.5);
        });
        break;
      }
      case 'SNARE': {
        const held = heldOpacityAt(t, 0.75);
        opacity(held);
        // The spikes grow in over the first fifth and then simply hold: the
        // cage is a state, and a state that keeps animating reads as an
        // effect being re-applied.
        const grow = Math.min(1, t * 5);
        parts.spikes?.children.forEach((spike, index) => {
          spike.scale.setScalar(grow);
          spike.rotation.y = index + effect.elapsedMs / 900;
        });
        parts.ring?.scale.setScalar(0.75 * (0.6 + grow * 0.4));
        break;
      }
      case 'LINK': {
        if (!origin || !target || !parts.beam) return false;
        // Drawn between the two live positions every frame — a taunt aimed at
        // where the skeleton *was* is a line into empty floor.
        const dx = target.x - origin.x;
        const dz = target.z - origin.z;
        const length = Math.hypot(dx, dz);
        effect.group.position.set(origin.x, CHEST_M, origin.z);
        parts.beam.scale.set(1, Math.max(0.01, length), 1);
        // The unit beam points along +Y; aim it down the line and slide it to
        // the midpoint, since a cylinder is centred on its own origin.
        parts.beam.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, 0, dz).normalize(),
        );
        parts.beam.position.set(dx / 2, 0, dz / 2);
        opacity(burstOpacityAt(t, 0.85));
        break;
      }
    }
    return t < 1 || (request.kind === 'BOLT' && !effect.landed);
  }

  private disposeEffect(effect: LiveEffect): void {
    this.root.remove(effect.group);
    for (const material of effect.materials) material.dispose();
  }

  /** How many effects are currently drawn — for tests and the debug HUD. */
  get liveCount(): number {
    return this.live.length;
  }

  dispose(): void {
    for (const effect of this.live) this.disposeEffect(effect);
    this.live.length = 0;
    this.scene.remove(this.root);
    // The layer owns its sprite set (see the constructor): one layer per
    // scene, loaded for that layer, so nothing else is still holding these.
    for (const texture of Object.values(this.sprites)) texture?.dispose();
  }
}

/** Effects that are meaningless without both ends alive. */
const NEEDS_BOTH_ENDS: ReadonlySet<AbilityFxKind> = new Set<AbilityFxKind>(['BOLT', 'LINK']);
